import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import type { CourseLesson, LessonSection, LessonSectionChoice } from '../../types/lesson';
import './lesson.css';

interface LessonPlayerProps {
  moduleTitle: string;
  lesson: CourseLesson;
  score: number;
  onBack: () => void;
  onFinish: () => void;
  onAwardPoints: (points: number) => Promise<void>;
}

interface ContentStep {
  id: string;
  type: 'content';
  title: string;
  body: string;
}

interface QuizStep {
  id: string;
  type: 'quiz';
  reference: string;
  title: string;
  body: string;
  choices: LessonSectionChoice[];
  evaluationMode: 'single' | 'multi';
  maxSelections: number | null;
  maxPoints: number;
  solution: string | null;
  tips: string | null;
  pointsExplanation: string | null;
}

type LessonStep = ContentStep | QuizStep;

type AnswerState = {
  selected: string[];
  isCorrect: boolean;
  earnedPoints: number;
};

const STEP_LABELS: Record<LessonStep['type'], string> = {
  content: 'Cours',
  quiz: 'Question',
};

const buildLessonSteps = (lesson: CourseLesson): LessonStep[] => {
  const steps: LessonStep[] = [];
  const sortedSections = lesson.sections.slice().sort((a, b) => a.position - b.position);
  const sectionByLabel = new Map(sortedSections.filter((section) => section.label).map((section) => [section.label as string, section]));
  const consumed = new Set<string>();

  const getMetadataStep = (section: LessonSection): string | null => {
    if (section.metadata && typeof section.metadata === 'object' && 'step' in section.metadata) {
      const value = (section.metadata as Record<string, unknown>).step;
      return typeof value === 'string' ? value : null;
    }
    return null;
  };

  for (const section of sortedSections) {
    if (consumed.has(section.id)) {
      continue;
    }

    if (section.kind === 'content') {
      const metadataStep = getMetadataStep(section);
      if (metadataStep === 'tips' || metadataStep === 'points' || metadataStep === 'reponses') {
        consumed.add(section.id);
        continue;
      }

      steps.push({
        id: section.id,
        type: 'content',
        title: section.title ?? section.label ?? lesson.title,
        body: section.body ?? '',
      });
      consumed.add(section.id);
      continue;
    }

    if (section.kind === 'quiz') {
      const reference = section.label ?? section.id;
      const solutionSection = sectionByLabel.get(`${reference}-reponses`);
      const tipsSection = sectionByLabel.get(`${reference}-tips`);
      const pointsSection = sectionByLabel.get(`${reference}-points`);

      if (solutionSection) {
        consumed.add(solutionSection.id);
      }
      if (tipsSection) {
        consumed.add(tipsSection.id);
      }
      if (pointsSection) {
        consumed.add(pointsSection.id);
      }

      const questionNumber = (() => {
        if (section.metadata && typeof section.metadata === 'object') {
          const value = (section.metadata as Record<string, unknown>).questionNumber;
          if (typeof value === 'number' || typeof value === 'string') {
            return String(value);
          }
        }
        return reference.replace(/[^0-9]/g, '') || reference;
      })();

      steps.push({
        id: section.id,
        type: 'quiz',
        reference,
        title: `Question ${questionNumber}`,
        body: section.body ?? '',
        choices: section.choices.slice().sort((a, b) => a.position - b.position),
        evaluationMode: section.evaluationMode,
        maxSelections: section.maxSelections,
        maxPoints: section.maxPoints,
        solution: solutionSection?.body ?? null,
        tips: tipsSection?.body ?? null,
        pointsExplanation: pointsSection?.body ?? null,
      });

      consumed.add(section.id);
    }
  }

  return steps;
};

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

const normalizeSelection = (values: string[]) => values.slice().sort((a, b) => a.localeCompare(b));

const LessonPlayer: React.FC<LessonPlayerProps> = ({ moduleTitle, lesson, score, onBack, onFinish, onAwardPoints }) => {
  const steps = useMemo(() => buildLessonSteps(lesson), [lesson]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [showTips, setShowTips] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);

  useEffect(() => {
    setSelectedChoices([]);
    setSubmissionError(null);
    setShowTips(false);
    scrollCardIntoView();
  }, [currentIndex]);

  useEffect(() => {
    const step = steps[currentIndex];
    if (!step || step.type !== 'quiz') {
      return;
    }
    const existing = answers[step.id];
    if (existing) {
      setSelectedChoices(existing.selected);
    }
  }, [answers, currentIndex, steps]);

  if (steps.length === 0) {
    return (
      <div className="lesson-player">
        <div className="lesson-player__empty">
          <h2>Aucun contenu disponible</h2>
          <p>Le module sera bientot disponible.</p>
        </div>
      </div>
    );
  }

  const step = steps[currentIndex];
  const progress = ((currentIndex + 1) / steps.length) * 100;
  const answerState = step.type === 'quiz' ? answers[step.id] : undefined;
  const isAnswered = Boolean(answerState);

  const handleContinue = () => {
    if (currentIndex >= steps.length - 1) {
      onFinish();
      return;
    }
    setCurrentIndex((previous) => Math.min(previous + 1, steps.length - 1));
  };

  const toggleChoice = (choiceId: string) => {
    if (step.type !== 'quiz' || isAnswered || isSubmittingAnswer) {
      return;
    }

    if (step.evaluationMode === 'multi') {
      setSelectedChoices((prev) => {
        if (prev.includes(choiceId)) {
          return prev.filter((value) => value !== choiceId);
        }
        if (typeof step.maxSelections === 'number' && prev.length >= step.maxSelections) {
          return prev;
        }
        return [...prev, choiceId];
      });
      return;
    }

    setSelectedChoices([choiceId]);
  };

  const handleSubmit = async () => {
    if (step.type !== 'quiz' || isAnswered || isSubmittingAnswer) {
      return;
    }

    if (selectedChoices.length === 0) {
      setSubmissionError('Selectionnez au moins une reponse.');
      return;
    }

    const correctIds = normalizeSelection(step.choices.filter((choice) => choice.isCorrect).map((choice) => choice.id));
    const normalizedSelected = normalizeSelection(selectedChoices);
    const isCorrect = arraysEqual(correctIds, normalizedSelected);
    const earnedPoints = isCorrect ? step.maxPoints : 0;

    try {
      setIsSubmittingAnswer(true);
      if (earnedPoints > 0) {
        await onAwardPoints(earnedPoints);
      }
      setAnswers((previous) => ({
        ...previous,
        [step.id]: {
          selected: normalizedSelected,
          isCorrect,
          earnedPoints,
        },
      }));
      setSubmissionError(null);
    } catch (error) {
      setSubmissionError(
        error instanceof Error ? error.message : "Impossible de mettre a jour le score pour le moment.",
      );
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const renderContentStep = (contentStep: ContentStep) => (
    <div className="lesson-player__card" data-lesson-card="active">
      <div className="lesson-player__card-scroll">
        <h2>{contentStep.title}</h2>
        <div className="lesson-player__content-text">{contentStep.body}</div>
      </div>
    </div>
  );

  const renderOption = (choice: LessonSectionChoice) => {
    if (step.type !== 'quiz') {
      return null;
    }

    const isSelected = selectedChoices.includes(choice.id);
    const showResult = isAnswered;
    let result: 'correct' | 'incorrect' | undefined;

    if (showResult) {
      if (choice.isCorrect) {
        result = 'correct';
      } else if (isSelected) {
        result = 'incorrect';
      }
    }

    const inputType = step.evaluationMode === 'multi' ? 'checkbox' : 'radio';

    return (
      <label
        key={choice.id}
        className="lesson-player__option"
        data-selected={isSelected ? 'true' : 'false'}
        data-result={result}
        data-disabled={showResult || isSubmittingAnswer ? 'true' : 'false'}
      >
        <input
          type={inputType}
          checked={isSelected}
          onChange={() => toggleChoice(choice.id)}
          disabled={showResult || isSubmittingAnswer}
        />
        <span>{choice.label}</span>
      </label>
    );
  };

  const renderQuizStep = (quizStep: QuizStep) => {
    const feedbackClassName = answerState?.isCorrect
      ? 'lesson-player__feedback lesson-player__feedback--success'
      : 'lesson-player__feedback lesson-player__feedback--error';
    const feedbackMessage = answerState?.isCorrect
      ? `Correct ! +${answerState.earnedPoints} point${answerState.earnedPoints > 1 ? 's' : ''}.`
      : "Ce n'est pas la bonne reponse. Aucun point n'est attribue cette fois-ci.";
    const solutionText = quizStep.solution
      ? quizStep.solution.replace(/^\s*Correction\s*:?/i, 'La bonne reponse est :')
      : null;
    const solutionHeading = solutionText ? solutionText.split('\n')[0] : null;
    const solutionDetails = solutionText && solutionText.includes('\n')
      ? solutionText.split('\n').slice(1).join('\n')
      : null;

    return (
      <div className="lesson-player__card" data-lesson-card="active">
        {quizStep.tips ? (
          <button
            type="button"
            className="lesson-player__tips"
            aria-label="Afficher un indice"
            onClick={() => setShowTips(true)}
            disabled={!quizStep.tips}
          >
            ?
          </button>
        ) : null}

        <div className="lesson-player__card-scroll">
          <h2>{quizStep.title}</h2>
          <div className="lesson-player__content-text">{quizStep.body}</div>

          {quizStep.evaluationMode === 'multi' ? (
            <p className="lesson-player__content-text" style={{ fontSize: '14px', color: '#6b7280', marginTop: '12px' }}>
              Selectionnez {quizStep.maxSelections ? `${quizStep.maxSelections} reponse${quizStep.maxSelections > 1 ? 's' : ''}` : 'toutes les reponses justes'}.
            </p>
          ) : null}

          <div className="lesson-player__options">
            {quizStep.choices.map(renderOption)}
          </div>

          {submissionError && <p className="lesson-player__feedback lesson-player__feedback--error">{submissionError}</p>}

          {answerState ? (
            <div className={feedbackClassName}>
              <p>{feedbackMessage}</p>
              {!answerState.isCorrect && solutionText ? (
                <div className="lesson-player__solution">
                  {solutionHeading ? <strong>{solutionHeading}</strong> : null}
                  {solutionDetails ? <span>{solutionDetails}</span> : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const handlePrimaryAction = () => {
    if (step.type === 'quiz' && !isAnswered) {
      void handleSubmit();
      return;
    }
    handleContinue();
  };

  const scrollCardIntoView = () => {
    const element = document.querySelector('[data-lesson-card="active"]');
    if (element instanceof HTMLElement) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const primaryActionLabel = (() => {
    if (step.type === 'quiz') {
      if (isAnswered) {
        return currentIndex >= steps.length - 1 ? 'Terminer' : 'Continuer';
      }
      return step.evaluationMode === 'multi' ? 'Valider mes reponses' : 'Valider';
    }
    return currentIndex >= steps.length - 1 ? 'Terminer' : 'Continuer';
  })();

  const primaryActionDisabled = step.type === 'quiz' && !isAnswered && selectedChoices.length === 0;
  const isLastStep = currentIndex >= steps.length - 1;

  return (
    <div className="lesson-player">
      <header className="lesson-player__header">
        <div className="lesson-player__header-row">
          <div className="lesson-player__nav-group">
            <button type="button" className="lesson-player__back" onClick={onBack} aria-label="Revenir au tableau de bord">
              ←
            </button>
          </div>
          <div className="lesson-player__title">{moduleTitle}</div>
          <div className="lesson-player__score">Score : {score}</div>
        </div>

        <div className="lesson-player__step-info">
          <span>
            {String(currentIndex + 1).padStart(2, '0')} {STEP_LABELS[step.type]}
          </span>
          <span>
            {currentIndex + 1} / {steps.length}
          </span>
        </div>

        <div className="lesson-player__progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
          <div className="lesson-player__progress-bar" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <div className="lesson-player__body">
        {step.type === 'content' ? renderContentStep(step) : renderQuizStep(step)}
      </div>

      <div className="lesson-player__footer-wrapper">
        <footer className="lesson-player__footer">
          <button
            type="button"
            className="lesson-player__primary-btn"
            onClick={handlePrimaryAction}
            disabled={isSubmittingAnswer || (step.type === 'quiz' && !isAnswered && primaryActionDisabled)}
          >
            {isLastStep && step.type === 'quiz' && !isAnswered ? primaryActionLabel : isLastStep && step.type === 'content' ? 'Terminer' : primaryActionLabel}
          </button>
        </footer>
      </div>

      {showTips && step.type === 'quiz' && step.tips ? (
        <div className="lesson-player__modal" role="dialog" aria-modal="true">
          <div className="lesson-player__modal-card">
            <h3>Astuce</h3>
            <p>{step.tips}</p>
            <button type="button" className="lesson-player__modal-close" onClick={() => setShowTips(false)}>
              Fermer
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LessonPlayer;
